import useOptionalRef from '@shared/hooks/use-optional-ref';
import clsx from 'clsx';
import type { ForwardedRef, ReactElement, ReactNode } from 'react';
import * as React from 'react';
import { forwardRef } from 'react';
import type { StyleProps } from '../../utils';
import { makeRootClassName } from '../../utils';

// prop types

type AllowedElements = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type TextType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body-lg'
  | 'body-md'
  | 'body-sm'
  | 'body-xs'
  | 'custom';

export type TextProps = StyleProps &
  Omit<
    React.HTMLAttributes<HTMLElement>,
    'children' | 'className' | 'style'
  > & {
    /**
     * The typography style to use.
     * @default "body-md"
     */
    type?: TextType;

    /**
     * Whether to use heavy styles, for body typographies.
     * @default false
     */
    isHeavy?: boolean;

    /** Which element the text should be rendered as. */
    as?: AllowedElements;

    /** The text to render. */
    children?: ReactNode;
  };

type PolymorphicTextProps = {
  as?: AllowedElements;
} & React.HTMLAttributes<
  HTMLHeadingElement | HTMLParagraphElement | HTMLSpanElement
> & { forwardedRef?: React.Ref<any> };

// config

const ROOT = makeRootClassName('Text');

const DEFAULT_PROPS = {
  type: 'body-md',
  isHeavy: false,
} as const;

const DEFAULT_ELEMENT = 'p';
const DEFAULT_ELEMENT_BY_TYPE: Record<
  NonNullable<TextProps['type']>,
  NonNullable<TextProps['as']>
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  'body-lg': DEFAULT_ELEMENT,
  'body-md': DEFAULT_ELEMENT,
  'body-sm': DEFAULT_ELEMENT,
  'body-xs': DEFAULT_ELEMENT,
  custom: DEFAULT_ELEMENT,
} as const;

// helpers

const PolymorphicText = React.forwardRef<
  HTMLHeadingElement | HTMLParagraphElement | HTMLSpanElement,
  PolymorphicTextProps
>(function PolymorphicText(
  /* eslint-disable-next-line react/prop-types,
  @typescript-eslint/naming-convention */
  { as: Comp = 'p', forwardedRef, ...props },
  ref
) {
  const componentRef = forwardedRef || ref;
  return <Comp {...props} ref={componentRef} />;
});

// main

function TextComponent(
  {
    type = DEFAULT_PROPS.type,
    isHeavy = DEFAULT_PROPS.isHeavy,
    as,
    className,
    children,
    ...rest
  }: TextProps,
  ref: ForwardedRef<HTMLElement>
): ReactElement {
  const domRef = useOptionalRef(ref);
  const elementType = as ?? DEFAULT_ELEMENT_BY_TYPE[type];
  const typeClassName =
    type.startsWith('body-') && isHeavy ? `${type}-heavy` : type;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <PolymorphicText
      {...rest}
      forwardedRef={domRef}
      as={elementType}
      className={clsx(`${ROOT} type-${typeClassName}`, className)}
    >
      {children}
    </PolymorphicText>
  );
}

/**
 * Styled text. Supports specifying the rendered element via the `as` prop.
 */
const Text = forwardRef<HTMLElement, TextProps>(TextComponent);

export default Text;
